import { Spinner } from "@dash/spinner";
import { useToast } from "@dash/toast";
import { useTheme } from "@emotion/react";
import { useState } from "react";
import { MdOutlineFileDownload } from "react-icons/md";

import { useServiceClient } from "@megaron/react-clients";

import { downloadInvoiceThreadsCsv } from "./invoiceCsv";
import { fetchAllThreadsForCsv } from "./fetchAllThreadsForCsv";
import { buildThreadSearchInput } from "./threadSearchInput";
import { Filters } from "./useThreadFilters";

type Props = {
  filters: Filters;
};

export const DownloadCsvButton: React.FC<Props> = ({ filters }) => {
  const theme = useTheme();
  const toast = useToast();
  const docs = useServiceClient("docs");
  const [isFetching, setIsFetching] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | undefined>();

  const handleDownload = async () => {
    setIsFetching(true);
    setProgressLabel(undefined);

    const result = await fetchAllThreadsForCsv(
      docs,
      buildThreadSearchInput(filters),
      (loadedCount, totalCount) => {
        if (totalCount !== undefined) {
          setProgressLabel(`${loadedCount}/${totalCount}`);
        }
      },
    );

    setIsFetching(false);
    setProgressLabel(undefined);

    if (result.isFailure) {
      toast.error(result.error);
      return;
    }

    if (result.value.items.length === 0) {
      toast.error("Brak faktur do eksportu");
      return;
    }

    if (result.value.wasTruncated) {
      toast.info(`Eksport ograniczony do ${result.value.items.length} faktur`);
    }

    downloadInvoiceThreadsCsv(result.value.items);
  };

  return (
    <button
      onClick={handleDownload}
      css={{
        display: "flex",
        gap: "0.25rem",
        alignItems: "center",
        border: "none",
        background: "none",
        padding: "0.375rem 0.625rem",
        color: theme.colors.primary,
        cursor: "pointer",
      }}
    >
      {isFetching ? (
        <div css={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <div css={{ width: "20px" }}>
            <Spinner size="16px" />
          </div>
          {progressLabel && <span css={{ fontSize: "0.75rem" }}>{progressLabel}</span>}
        </div>
      ) : (
        <MdOutlineFileDownload size={20} />
      )}
      CSV
    </button>
  );
};
